'use client';

import { MutableRefObject, PropsWithChildren, createContext, useContext, useRef } from 'react';

import { isFunction } from 'lodash';

import { useScrollTo } from '@/hooks/useScrollTo';
import { isFiniteNumber } from '@/utils/number';

type FaqItemId = string | number;
const FaqContext = createContext<{
  faqBlockRef?: MutableRefObject<HTMLDivElement | null>;
  scrollToFaq: (faqItemId: FaqItemId) => void;
  registerFaqItem: (
    faqItemId: FaqItemId,
    faqItemOpenFunction: Function,
    ref: MutableRefObject<HTMLDivElement>,
  ) => void;
}>({
  scrollToFaq: () => {
    console.error('No FAQProvider detected up in the tree');
  },
  registerFaqItem: () => {},
});

type FaqItemsRegistry = Record<FaqItemId, { openFunction: Function; ref: MutableRefObject<HTMLDivElement> }>;

export function FAQProvider({ children }: PropsWithChildren) {
  const faqBlockRef = useRef<HTMLDivElement | null>(null);
  const { scrollToElement } = useScrollTo();
  const faqItemsRegistry = useRef<FaqItemsRegistry>({});

  function scrollToFaqSection() {
    if (!(faqBlockRef.current instanceof HTMLElement)) {
      return console.error(
        new Error(`FAQProvider ref has not been assigned to any element, ref: ${faqBlockRef.current}`),
      );
    }
    scrollToElement(faqBlockRef);
  }

  function scrollToSpecificFAQItemAndOpenIt(faqItemToOpenId: FaqItemId) {
    if (!isFiniteNumber(faqItemToOpenId) && typeof faqItemToOpenId !== 'string') {
      return console.error(
        new Error(
          `FAQProvider scrollToSpecificItem, wrong arg faqItemToOpenId value ${faqItemToOpenId}, its should be of type FaqItemId (string or number)`,
        ),
      );
    }

    const faqItem = faqItemsRegistry?.current?.[faqItemToOpenId];
    if (faqItem?.ref?.current instanceof HTMLElement) {
      scrollToElement(faqItem?.ref);
    } else {
      console.error(
        new Error(
          `FAQProvider, tried to scrollToSpecificItem, but item ref is incorrect, faq item ${faqItemToOpenId} ref: ${faqItem?.ref?.current}`,
        ),
      );
    }

    if (isFunction(faqItemsRegistry?.current?.[faqItemToOpenId]?.openFunction)) {
      faqItemsRegistry?.current?.[faqItemToOpenId]?.openFunction();
    } else {
      console.error(new Error(`FAQProvider, failed to open faq item ${faqItemToOpenId}, wrong openFunction`));
    }
  }

  return (
    <FaqContext.Provider
      value={{
        faqBlockRef,
        scrollToFaq: (faqItemId) => {
          try {
            if (faqItemId) {
              scrollToSpecificFAQItemAndOpenIt(faqItemId);
            } else {
              scrollToFaqSection();
            }
          } catch (e) {
            console.error(new Error('FAQProvider: scrollToFaq failed', { cause: e }));
          }
        },
        registerFaqItem: (faqItemId, openFaqItemFunction, ref) => {
          faqItemsRegistry.current[faqItemId.toString()] = { openFunction: openFaqItemFunction, ref };
        },
      }}
    >
      {children}
    </FaqContext.Provider>
  );
}

export function useFaqController() {
  return { faqController: useContext(FaqContext) };
}
